import React, { useState } from 'react';
import { btnStyles } from './Hero';

export function AppThumb({ app, big = false }) {
  return (
    <div style={{
      aspectRatio: big ? '16 / 10' : '4 / 3',
      borderRadius: big ? 24 : 12,
      background: app.color,
      position: 'relative',
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <span style={{
        fontFamily: 'var(--font-display)',
        fontStyle: 'italic',
        fontSize: big ? 220 : 96,
        lineHeight: 1,
        color: app.glyphFg,
        opacity: 0.95,
      }}>{app.glyph}</span>
      <span style={{
        position: 'absolute', bottom: big ? 24 : 12, right: big ? 24 : 12,
        width: big ? 14 : 10, height: big ? 14 : 10, borderRadius: '50%',
        background: '#FF4D00',
        boxShadow: app.status === 'Now shipping' ? '0 0 0 4px #FF4D0033' : 'none',
      }} />
    </div>
  );
}

function AppCard({ app, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <article
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: 'var(--bg-elev)',
        border: '1px solid',
        borderColor: hover ? 'var(--border-strong)' : 'var(--border)',
        borderRadius: 16, padding: 18,
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hover ? '0 4px 12px rgba(10,10,10,0.06)' : 'none',
        transition: 'all 220ms var(--ease-out)',
        cursor: 'pointer',
      }}>
      <div style={{ marginBottom: 16 }}><AppThumb app={app} /></div>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 10,
        letterSpacing: '0.14em', textTransform: 'uppercase',
        color: 'var(--fg-muted)', marginBottom: 8,
      }}>
        {app.year} · {app.status}
      </div>
      <h3 style={{
        fontFamily: 'var(--font-sans)', fontSize: 22, fontWeight: 600,
        margin: '0 0 6px', letterSpacing: '-0.01em',
      }}>{app.name}</h3>
      <p style={{
        fontFamily: 'var(--font-sans)', fontSize: 14, lineHeight: 1.5,
        color: 'var(--fg-muted)', margin: 0,
      }}>{app.tagline}</p>
    </article>
  );
}

function EmptyPortfolio() {
  return (
    <div style={{
      gridColumn: '1 / -1',
      border: '1px dashed var(--border-strong)', borderRadius: 16,
      padding: '64px 32px', textAlign: 'center', background: 'var(--bg-elev)',
    }}>
      <div style={{
        fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.18em',
        textTransform: 'uppercase', color: 'var(--fg-muted)', marginBottom: 16,
      }}>Nothing here yet</div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 36, lineHeight: 1.1, marginBottom: 12 }}>
        The shelf is <em style={{ color: 'var(--accent)' }}>empty</em>.
      </div>
      <p style={{ color: 'var(--fg-muted)', maxWidth: 380, margin: '0 auto 24px' }}>
        Head to the admin portal to add the first app.
      </p>
    </div>
  );
}

export default function PortfolioGrid({ apps, onOpen }) {
  return (
    <section id="portfolio" style={{ background: 'var(--bg)', padding: '96px 32px', scrollMarginTop: 80 }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 48, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: 'var(--fg-muted)', marginBottom: 16,
            }}>01 — Portfolio</div>
            <h2 style={{
              fontFamily: 'var(--font-display)', fontWeight: 400,
              fontSize: 'clamp(48px, 6vw, 80px)', lineHeight: 1, letterSpacing: '-0.03em',
              margin: 0, maxWidth: 720,
            }}>
              {apps.length} {apps.length === 1 ? 'app' : 'apps'}. <em style={{ color: 'var(--accent)' }}>One studio</em>.
            </h2>
          </div>
          <p style={{ fontSize: 15, color: 'var(--fg-muted)', maxWidth: 320, lineHeight: 1.6 }}>
            We ship apps with care. Each one stays small enough to keep, and stays around long enough to matter.
          </p>
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 18,
        }}>
          {apps.length === 0
            ? <EmptyPortfolio />
            : apps.map((a) => <AppCard key={a.id} app={a} onClick={() => onOpen(a.id)} />)}
        </div>
      </div>
    </section>
  );
}

export function AppDetail({ app, onBack }) {
  if (!app) {
    return (
      <section style={{ padding: '120px 32px', textAlign: 'center' }}>
        <p style={{ color: 'var(--fg-muted)' }}>That app no longer exists.</p>
        <span onClick={onBack} style={{ cursor: 'pointer', color: 'var(--accent)' }}>← Back to portfolio</span>
      </section>
    );
  }
  return (
    <section style={{ background: 'var(--bg)', padding: '64px 32px 96px' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <span onClick={onBack} style={{
          fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.14em',
          textTransform: 'uppercase', color: 'var(--fg-muted)', cursor: 'pointer',
          display: 'inline-block', marginBottom: 32,
        }}>← Back to portfolio</span>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 64, alignItems: 'center' }}>
          <div>
            <div style={{
              fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 20,
            }}>{app.status} · {app.year}</div>
            <h1 style={{
              fontFamily: 'var(--font-display)', fontWeight: 400,
              fontSize: 'clamp(56px, 7vw, 96px)', lineHeight: 0.95, letterSpacing: '-0.04em',
              margin: '0 0 24px',
            }}>{app.name}.</h1>
            <p style={{
              fontFamily: 'var(--font-sans)', fontSize: 22, lineHeight: 1.4,
              color: 'var(--fg)', margin: '0 0 16px', fontWeight: 500,
            }}>{app.tagline}</p>
            <p style={{
              fontFamily: 'var(--font-sans)', fontSize: 17, lineHeight: 1.65,
              color: 'var(--fg-muted)', margin: '0 0 32px', maxWidth: 460,
            }}>{app.description}</p>
            <div style={{ display: 'flex', gap: 12 }}>
              {app.appleLink && (
                <a href={app.appleLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <button style={btnStyles.primary}>Open in App Store →</button>
                </a>
              )}
              {app.googleLink && (
                <a href={app.googleLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <button style={btnStyles.primary}>Get on Google Play →</button>
                </a>
              )}
              {app.websiteLink && (
                <a href={app.websiteLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <button style={btnStyles.ghost}>Visit website</button>
                </a>
              )}
            </div>
          </div>
          <div><AppThumb app={app} big /></div>
        </div>
      </div>
    </section>
  );
}
